import React, { useId, useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as Popover from '@radix-ui/react-popover';
import { ChevronDownIcon, CheckIcon, Cross2Icon, MagnifyingGlassIcon } from '@radix-ui/react-icons';
import { inputCls, inputErrorClass } from 'modules/Common/form/v2/primitives/inputClasses';
import Label from 'modules/Common/form/v2/primitives/Label';
import { usePopoverPortalContainer } from 'modules/Common/utils/overlayHost';
import { controlAnchorProps } from 'modules/Common/utils/gapAnchor';
import { TreeList, filterTree } from 'modules/Common/ui/CgTreeList';

// Sit above CgDialog and CgPopover (z-99999) so
// the dropdown isn't clipped when this combobox opens inside another popover
// (e.g. the column-filter popover in CgColumnFilter).
const POPOVER_Z_INDEX = 100050;

// Above this many options the dropdown shows a search input. Short lists
// (statuses, quarters, years) don't need one — the user can scan visually.
const SEARCHABLE_OPTION_THRESHOLD = 10;

// The horizontal inset every row in both dropdowns shares — search header,
// option rows, group labels, the empty and create rows, the selection footer.
// Written as an arbitrary value on purpose: `px-3` is a colliding step
// (App.scss compiles bootstrap.scss in, and Bootstrap's `.px-3` is
// `1rem !important`), so it rendered 16px, and `!px-3` only wins on stylesheet
// source order. An arbitrary value has no Bootstrap twin, so it renders what it
// says — the remedy WizardControls documents. One constant so the select and
// the multi-select cannot drift apart again, which is how one was fixed and the
// other was not.
export const ROW_INSET_X = 'px-[12px]';

const defaultFilterOption = (option, input) => {
  if (!input) return true;
  const q = String(input).toLowerCase();
  return (
    String(option.label ?? '')
      .toLowerCase()
      .includes(q) ||
    String(option.value ?? '')
      .toLowerCase()
      .includes(q)
  );
};

const useSearchState = (open, inputRef) => {
  const [query, setQuery] = useState('');
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
    setQuery('');
  }, [open, inputRef]);
  return [query, setQuery];
};

const SearchHeader = ({
  inputRef,
  query,
  setQuery,
  onEnter,
  onArrowDown,
  onArrowUp,
  listId,
  activeId,
  placeholder,
}) => {
  const { t } = useTranslation('common');
  return (
    <div
      className={`flex items-center gap-2 ${ROW_INSET_X} py-2 border-b border-slate-100 shrink-0`}
    >
      <MagnifyingGlassIcon className="text-slate-400 shrink-0" />
      <input
        ref={inputRef}
        value={query}
        aria-controls={listId}
        aria-activedescendant={activeId}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && onEnter) {
            e.preventDefault();
            onEnter();
          } else if (e.key === 'ArrowDown' && onArrowDown) {
            e.preventDefault();
            onArrowDown();
          } else if (e.key === 'ArrowUp' && onArrowUp) {
            e.preventDefault();
            onArrowUp();
          }
        }}
        placeholder={placeholder ?? t('filter.searchPlaceholder', 'Search...')}
        className="min-w-0 w-full flex-1 text-sm bg-transparent border-0 p-0 focus:outline-none focus:ring-0 placeholder:text-slate-400"
      />
    </div>
  );
};

// The trigger's two sizes. Default is the v2 form field. `sm` is for hosts
// whose row height is the layout — an editable-grid cell is ~26px, and a
// full-size field there makes the row jump the moment it is opened.
const TRIGGER_LAYOUT = 'flex items-center justify-between gap-2';
const SM_TRIGGER_CLASS =
  'w-full text-xs border border-slate-300 rounded bg-white px-1.5 py-0.5 transition-colors ' +
  'focus:outline-none focus:ring-1 focus:ring-primaryColor/40 ' +
  'disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed';

const popoverContentProps = {
  align: 'start',
  sideOffset: 4,
  // Keep the panel inside the viewport on short screens: Radix flips/shifts
  // to avoid collisions and exposes the remaining space as
  // --radix-popover-content-available-height, which caps the panel so the
  // list scrolls instead of overflowing the screen edge (the clipping bug).
  collisionPadding: 8,
  style: {
    width: 'var(--radix-popover-trigger-width)',
    minWidth: 'var(--radix-popover-trigger-width)',
    maxHeight: 'var(--radix-popover-content-available-height)',
    zIndex: POPOVER_Z_INDEX,
  },
  className: 'flex flex-col bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden',
};

/**
 * Single-select combobox built on @radix-ui/react-popover with a typeahead
 * search input. Drop-in replacement for the react-select dropdowns used by
 * CgSearchFilter — accepts raw values (not {value,label} objects).
 *
 * Options may be flat ({ value, label, isDisabled? }) or grouped
 * ({ label, options: [...] }) — groups render a small heading, disabled
 * options render muted and unclickable.
 */
export const CgFilterSelect = ({
  label,
  required = false,
  options = [],
  value,
  onChange,
  placeholder,
  isClearable = true,
  filterOption,
  searchable,
  className = '',
  disabled = false,
  error = false,
  // Creatable mode (react-select CreatableSelect stand-in): typing a value
  // that matches no option offers a "create" row which calls onChange with
  // the raw typed string. The current value is displayed even when it isn't
  // one of the options. Forces the search input on.
  allowCreate = false,
  formatCreateLabel = (input) => `Use "${input}"`,
  // Creatable mode: more ways to use what was typed, offered ABOVE the plain "use it" row —
  // `(input) => [{ value, label }]`. Enter on a query that matches nothing picks the first of them.
  createOptions,
  // With `createOptions`, the line above the create rows when nothing matches: `(input) => string`.
  // Without one, the generic "No matches for …" is shown — it describes the plain "use it" row.
  createHint,
  // Optional custom renderer for a dropdown row's body: (option) => ReactNode.
  // `label` must stay a plain string (the trigger and the default filter read
  // it as text) — this only enriches the row, so keep the returned node
  // NON-INTERACTIVE: rows are <button>s and nesting a button/popover trigger
  // inside one is invalid HTML and swallows the row's own click.
  renderOption,
  // External id for the trigger, so a host-rendered <Label htmlFor> (e.g.
  // Form v2's FormField) associates with — and names — the trigger button.
  id,
  // Trigger footprint: 'md' is the v2 form field, 'sm' fits an editable-grid
  // cell without changing the row height.
  size = 'md',
  // Open the list as soon as this mounts. For a host that has already spent
  // the user's click getting here — a grid cell entering edit mode on a
  // double-click — where a closed combobox would cost a third click.
  defaultOpen = false,
  // Told whenever the panel opens or closes, INCLUDING a dismissal (Escape,
  // click away). A host that mounted this for one interaction uses it to end
  // that interaction when the user walks away from it.
  onOpenChange,
  // Widen (or narrow) the panel independently of the trigger. By default the
  // panel matches the trigger, which is right for a form field and wrong for
  // a 140px grid cell whose options are sentences.
  menuWidth,
  // Grouped options render as a collapsible tree: categories first, their options inside.
  tree = false,
  anchor,
  empty,
}) => {
  const { t } = useTranslation('common');
  const placeholderText = placeholder ?? t('fields.selectPlaceholder', 'Select...');
  const autoId = useId();
  const triggerId = id ?? autoId;
  const listId = `${autoId}-list`;
  const [open, setOpen] = useState(defaultOpen);
  const [activeState, setActiveState] = useState(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const [query, setQuery] = useSearchState(open, inputRef);
  const [wrapperRef, portalContainer] = usePopoverPortalContainer();

  const handleOpenChange = (next) => {
    setOpen(next);
    setActiveState(null);
    onOpenChange?.(next);
  };

  const triggerClass =
    size === 'sm'
      ? [SM_TRIGGER_CLASS, error ? inputErrorClass : '', TRIGGER_LAYOUT].filter(Boolean).join(' ')
      : inputCls(error, TRIGGER_LAYOUT);

  // `maxWidth` keeps an explicit width honest on a narrow viewport — Radix
  // shifts the panel to avoid a collision but will not shrink it.
  const contentStyle = menuWidth
    ? { ...popoverContentProps.style, width: menuWidth, minWidth: menuWidth, maxWidth: '90vw' }
    : popoverContentProps.style;

  // Normalize grouped and flat inputs into one grouped shape; a null group
  // label means "render without a heading" (the flat case).
  const groups = useMemo(() => {
    const hasGroups = options.some((o) => Array.isArray(o?.options));
    return hasGroups
      ? options.map((g) => ({
          key: g.key ?? null,
          label: g.label ?? null,
          description: g.description ?? null,
          options: g.options ?? [],
        }))
      : [{ label: null, options }];
  }, [options]);

  const flatOptions = useMemo(() => groups.flatMap((g) => g.options), [groups]);
  const showSearch =
    allowCreate || (searchable ?? flatOptions.length > SEARCHABLE_OPTION_THRESHOLD);

  const selected = useMemo(() => {
    const match = flatOptions.find((o) => o.value === value) ?? null;
    // Creatable values aren't in the option list — surface them anyway.
    if (!match && allowCreate && value != null && value !== '') {
      return { value, label: String(value) };
    }
    return match;
  }, [flatOptions, value, allowCreate]);

  const filteredGroups = useMemo(() => {
    const fn = filterOption || defaultFilterOption;
    if (tree) return filterTree(groups, showSearch ? query : '', fn);
    if (!showSearch || !query) return groups;
    return groups
      .map((g) => ({
        ...g,
        options: g.options.filter((o) => fn({ label: o.label, value: o.value, data: o }, query)),
      }))
      .filter((g) => g.options.length > 0);
  }, [groups, filterOption, query, showSearch, tree]);

  const filteredCount = useMemo(
    () => filteredGroups.reduce((sum, g) => sum + g.options.length, 0),
    [filteredGroups],
  );

  // Offer the create row only for a non-empty query that isn't already an
  // option (by label or value, case-insensitive).
  const createCandidate = useMemo(() => {
    if (!allowCreate) return null;
    const trimmed = query.trim();
    if (!trimmed) return null;
    const q = trimmed.toLowerCase();
    const exists = flatOptions.some(
      (o) => String(o.label ?? '').toLowerCase() === q || String(o.value ?? '').toLowerCase() === q,
    );
    return exists ? null : trimmed;
  }, [allowCreate, query, flatOptions]);

  const pick = (v) => {
    onChange?.(v);
    setOpen(false);
    setActiveState(null);
  };
  const commitCreate = () => {
    if (createCandidate) pick(createCandidate);
  };
  const extraCreateRows = createCandidate && createOptions ? createOptions(createCandidate) : [];

  const selectableOptions = filteredGroups.flatMap((g) => g.options).filter((o) => !o.isDisabled);
  const rowValues = [
    ...selectableOptions.map((o) => o.value),
    ...extraCreateRows.map((r) => r.value),
    ...(createCandidate ? [createCandidate] : []),
  ];
  const optionRowIndex = new Map(selectableOptions.map((o, i) => [o.value, i]));
  const extraRowOffset = selectableOptions.length;
  const plainRowIndex = extraRowOffset + extraCreateRows.length;
  const exactQuery = query.trim().toLowerCase();
  const exactIndex = exactQuery
    ? selectableOptions.findIndex((o) => String(o.label ?? '').toLowerCase() === exactQuery)
    : -1;
  const wantedIndex =
    activeState && activeState.forQuery === query ? activeState.index : Math.max(exactIndex, 0);
  const activeIndex = Math.min(wantedIndex, rowValues.length - 1);
  const rowId = (i) => `${listId}-row-${i}`;
  const activeId = !tree && activeIndex >= 0 ? rowId(activeIndex) : undefined;
  const moveActive = (delta) => {
    if (!rowValues.length) return;
    const next = Math.min(Math.max(activeIndex + delta, 0), rowValues.length - 1);
    setActiveState({ forQuery: query, index: next });
    requestAnimationFrame(() =>
      document.getElementById(rowId(next))?.scrollIntoView?.({ block: 'nearest' }),
    );
  };
  const activateRow = (i) => setActiveState({ forQuery: query, index: i });
  const activeRowClass = (i) => (i === activeIndex && !tree ? 'bg-primaryColor/5' : '');

  return (
    <div
      ref={wrapperRef}
      className={className}
    >
      {label ? (
        <Label
          htmlFor={triggerId}
          required={required}
        >
          {label}
        </Label>
      ) : null}
      <Popover.Root
        open={open}
        onOpenChange={handleOpenChange}
      >
        <Popover.Trigger asChild>
          <button
            id={triggerId}
            type="button"
            role="combobox"
            aria-label={label || id ? undefined : placeholder}
            aria-expanded={open}
            aria-haspopup={tree ? 'tree' : 'listbox'}
            aria-controls={open ? listId : undefined}
            disabled={disabled}
            {...controlAnchorProps(anchor, empty ?? !selected)}
            onKeyDown={(e) => {
              if (
                !open &&
                (e.key === 'Backspace' || e.key === 'Delete') &&
                isClearable &&
                selected
              ) {
                e.preventDefault();
                onChange?.(null);
              }
            }}
            className={triggerClass}
          >
            <span
              className={`truncate text-left ${selected ? 'text-slate-700' : 'text-slate-400'}`}
            >
              {selected ? selected.label : placeholderText}
            </span>
            <span className="flex items-center gap-1 text-slate-400 shrink-0">
              {isClearable && selected && !disabled && (
                <span
                  role="button"
                  tabIndex={-1}
                  onPointerDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onChange?.(null);
                  }}
                  className="hover:text-slate-600"
                  aria-label={t('common:actions.clear', 'Clear')}
                >
                  <Cross2Icon />
                </span>
              )}
              <ChevronDownIcon />
            </span>
          </button>
        </Popover.Trigger>
        <Popover.Portal container={portalContainer ?? undefined}>
          <Popover.Content
            {...popoverContentProps}
            data-cg-escape-closes=""
            style={contentStyle}
            onOpenAutoFocus={(e) => e.preventDefault()}
            onEscapeKeyDown={() => handleOpenChange(false)}
            // Closing hands focus back to the trigger — unless picking moved it somewhere on purpose
            // (a new meter's name box): Radix returns it a tick later and would take it back.
            onCloseAutoFocus={(e) => {
              const active = document.activeElement;
              if (active && active !== document.body && active.id !== triggerId) e.preventDefault();
            }}
          >
            {showSearch && (
              <SearchHeader
                inputRef={inputRef}
                query={query}
                setQuery={setQuery}
                // Creatable: say up front that a value of one's own is welcome, not only a search.
                placeholder={
                  allowCreate
                    ? t('filter.searchOrTypePlaceholder', 'Search, or type your own…')
                    : undefined
                }
                listId={listId}
                activeId={activeId}
                onArrowDown={
                  tree
                    ? () => listRef.current?.querySelector('[data-tree-row]')?.focus()
                    : () => moveActive(1)
                }
                onArrowUp={tree ? undefined : () => moveActive(-1)}
                onEnter={() => {
                  if (tree) {
                    if (!query.trim()) return;
                    const exact = exactIndex >= 0 ? selectableOptions[exactIndex] : null;
                    const matches = filterOption || defaultFilterOption;
                    const first =
                      exact ??
                      selectableOptions.find(
                        (o) =>
                          !o.isAction &&
                          matches({ label: o.label, value: o.value, data: o }, query),
                      );
                    if (first) {
                      pick(first.value);
                    } else if (extraCreateRows.length) {
                      pick(extraCreateRows[0].value);
                    } else if (createCandidate) {
                      commitCreate();
                    }
                    return;
                  }
                  if (activeIndex >= 0) pick(rowValues[activeIndex]);
                }}
              />
            )}
            {/* custom-scrollbar: without it macOS ("Show scroll bars: Always")
                reserves a ~15px classic gutter with a near-invisible thumb, so
                once the list overflows the rows stop short of the panel edge
                and it reads as unexplained white space. */}
            <div
              ref={listRef}
              id={tree ? undefined : listId}
              role={tree ? undefined : 'listbox'}
              className={`custom-scrollbar ${tree ? 'max-h-[320px]' : 'max-h-[260px]'} min-h-0 overflow-y-auto p-1`}
            >
              {filteredCount === 0 && !createCandidate ? (
                <div className={`${ROW_INSET_X} py-2 text-sm text-slate-400 text-center`}>
                  {t('common:filter.noMatches', 'No matches')}
                </div>
              ) : tree && filteredGroups.length > 0 ? (
                <TreeList
                  groups={filteredGroups}
                  searching={Boolean(query.trim())}
                  value={value}
                  onPick={pick}
                  id={listId}
                  renderOption={renderOption}
                  onExitTop={() => inputRef.current?.focus()}
                />
              ) : (
                filteredGroups.map((group, groupIndex) => (
                  <React.Fragment key={group.label ?? groupIndex}>
                    {group.label && (
                      <div
                        className={`${ROW_INSET_X} pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400`}
                      >
                        {group.label}
                      </div>
                    )}
                    {group.options.map((o) => {
                      const isSelected = o.value === value;
                      const index = optionRowIndex.get(o.value);
                      return (
                        <button
                          key={o.value}
                          id={index === undefined ? undefined : rowId(index)}
                          type="button"
                          role="option"
                          aria-selected={isSelected}
                          aria-disabled={o.isDisabled || undefined}
                          disabled={o.isDisabled}
                          onMouseEnter={index === undefined ? undefined : () => activateRow(index)}
                          onClick={() => {
                            onChange?.(o.value);
                            setOpen(false);
                          }}
                          className={`w-full flex items-start justify-between gap-2 ${ROW_INSET_X} py-2 text-sm rounded-md text-left focus:outline-none ${
                            o.isDisabled
                              ? 'text-slate-300 cursor-not-allowed'
                              : isSelected
                                ? // Selected sits a tint above hover (/10 vs /5) and
                                  // adds brand text + weight + check, so it still reads
                                  // as the chosen row even while another row is hovered.
                                  'bg-primaryColor/10 text-primaryColor font-medium hover:bg-primaryColor/15 focus:bg-primaryColor/15'
                                : 'text-slate-700 hover:bg-primaryColor/5 focus:bg-primaryColor/5'
                          } ${o.isDisabled || isSelected ? '' : activeRowClass(index)}`}
                        >
                          <span className="flex-1 break-words">
                            {renderOption ? renderOption(o) : o.label}
                          </span>
                          {isSelected && (
                            <CheckIcon className="text-primaryColor shrink-0 mt-0.5" />
                          )}
                        </button>
                      );
                    })}
                  </React.Fragment>
                ))
              )}
              {allowCreate && !query.trim() ? (
                <div className={`${ROW_INSET_X} py-2 text-xs text-slate-500`}>
                  {t('filter.typeYourOwnHint', 'Not listed? Type your own value.')}
                </div>
              ) : null}
              {createCandidate && filteredCount === 0 ? (
                <div className={`${ROW_INSET_X} pt-2 pb-1 text-xs text-slate-500`}>
                  {extraCreateRows.length && createHint
                    ? createHint(createCandidate)
                    : extraCreateRows.length
                      ? t('filter.noMatchesFor', {
                          defaultValue: 'No matches for “{{query}}”.',
                          query: createCandidate,
                        })
                      : t('filter.noMatchesTypeYourOwn', {
                          defaultValue:
                            'No matches for “{{query}}”. You can use it as your own value:',
                          query: createCandidate,
                        })}
                </div>
              ) : null}
              {extraCreateRows.map((row, i) => (
                <button
                  key={row.value}
                  id={rowId(extraRowOffset + i)}
                  type="button"
                  role="option"
                  aria-selected={false}
                  onMouseEnter={() => activateRow(extraRowOffset + i)}
                  onClick={() => pick(row.value)}
                  className={`w-full ${ROW_INSET_X} py-2 text-sm rounded-md text-left text-primaryColor font-medium hover:bg-primaryColor/5 focus:bg-primaryColor/5 focus:outline-none ${activeRowClass(extraRowOffset + i)}`}
                >
                  {row.label}
                </button>
              ))}
              {createCandidate && (
                <button
                  id={rowId(plainRowIndex)}
                  type="button"
                  role="option"
                  aria-selected={false}
                  onMouseEnter={() => activateRow(plainRowIndex)}
                  onClick={commitCreate}
                  className={`w-full ${ROW_INSET_X} py-2 text-sm rounded-md text-left text-primaryColor font-medium hover:bg-primaryColor/5 focus:bg-primaryColor/5 focus:outline-none ${activeRowClass(plainRowIndex)}`}
                >
                  {formatCreateLabel(createCandidate)}
                </button>
              )}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
};

/**
 * Multi-select combobox: same look as CgFilterSelect, but options toggle on
 * click and the popover stays open. `value` and onChange use a raw value
 * array.
 */
export const CgFilterMultiSelect = ({
  id,
  label,
  options = [],
  value = [],
  onChange,
  placeholder,
  isClearable = true,
  filterOption,
  searchable,
  className = '',
  disabled = false,
  error = false,
}) => {
  const { t } = useTranslation('common');
  const placeholderText = placeholder ?? t('fields.selectPlaceholder', 'Select...');
  const autoId = useId();
  const triggerId = id ?? autoId;
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const [query, setQuery] = useSearchState(open, inputRef);
  const [wrapperRef, portalContainer] = usePopoverPortalContainer();
  const showSearch = searchable ?? options.length > SEARCHABLE_OPTION_THRESHOLD;

  const selectedSet = useMemo(() => new Set(value ?? []), [value]);
  const selectedOptions = useMemo(
    () => options.filter((o) => selectedSet.has(o.value)),
    [options, selectedSet],
  );

  const filtered = useMemo(() => {
    if (!showSearch || !query) return options;
    const fn = filterOption || defaultFilterOption;
    return options.filter((o) => fn({ label: o.label, value: o.value, data: o }, query));
  }, [options, filterOption, query, showSearch]);

  const toggle = (v) => {
    if (selectedSet.has(v)) {
      onChange?.((value ?? []).filter((x) => x !== v));
    } else {
      onChange?.([...(value ?? []), v]);
    }
  };

  const triggerLabel =
    selectedOptions.length === 0
      ? placeholderText
      : selectedOptions.length === 1
        ? selectedOptions[0].label
        : t('common:filter.selectedCount', {
            count: selectedOptions.length,
            defaultValue: '{{count}} selected',
            defaultValue_other: '{{count}} selected',
          });

  return (
    <div
      ref={wrapperRef}
      className={className}
    >
      {label ? <Label htmlFor={triggerId}>{label}</Label> : null}
      <Popover.Root
        open={open}
        onOpenChange={setOpen}
      >
        <Popover.Trigger asChild>
          <button
            id={triggerId}
            type="button"
            disabled={disabled}
            className={inputCls(error, 'flex items-center justify-between gap-2')}
          >
            <span
              className={`truncate text-left ${selectedOptions.length > 0 ? 'text-slate-700' : 'text-slate-400'}`}
            >
              {triggerLabel}
            </span>
            <span className="flex items-center gap-1 text-slate-400 shrink-0">
              {isClearable && selectedOptions.length > 0 && !disabled && (
                <span
                  role="button"
                  tabIndex={-1}
                  onPointerDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onChange?.([]);
                  }}
                  className="hover:text-slate-600"
                  aria-label={t('common:actions.clear', 'Clear')}
                >
                  <Cross2Icon />
                </span>
              )}
              <ChevronDownIcon />
            </span>
          </button>
        </Popover.Trigger>
        <Popover.Portal container={portalContainer ?? undefined}>
          <Popover.Content
            {...popoverContentProps}
            data-cg-escape-closes=""
            onOpenAutoFocus={(e) => e.preventDefault()}
            onEscapeKeyDown={() => setOpen(false)}
          >
            {showSearch && (
              <SearchHeader
                inputRef={inputRef}
                query={query}
                setQuery={setQuery}
              />
            )}
            {/* custom-scrollbar: without it macOS ("Show scroll bars: Always")
                reserves a ~15px classic gutter with a near-invisible thumb, so
                once the list overflows the rows stop short of the panel edge
                and it reads as unexplained white space. */}
            <div className="custom-scrollbar max-h-[260px] min-h-0 overflow-y-auto p-1">
              {filtered.length === 0 ? (
                <div className={`${ROW_INSET_X} py-2 text-sm text-slate-400 text-center`}>
                  {t('common:filter.noMatches', 'No matches')}
                </div>
              ) : (
                filtered.map((o) => {
                  const isSelected = selectedSet.has(o.value);
                  return (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => toggle(o.value)}
                      className={`w-full flex items-start gap-2 ${ROW_INSET_X} py-2 text-sm rounded-md text-left focus:outline-none ${
                        isSelected
                          ? // Hover lives per-branch (not shared) so hovering a ticked
                            // row deepens its tint instead of overriding it with grey.
                            'bg-primaryColor/10 text-primaryColor font-medium hover:bg-primaryColor/15 focus:bg-primaryColor/15'
                          : 'text-slate-700 hover:bg-primaryColor/5 focus:bg-primaryColor/5'
                      }`}
                    >
                      <span
                        className={`inline-flex items-center justify-center w-4 h-4 rounded border shrink-0 mt-0.5 ${
                          isSelected
                            ? 'bg-primaryColor border-primaryColor text-white'
                            : 'border-slate-300 bg-white'
                        }`}
                      >
                        {isSelected && <CheckIcon className="w-3 h-3" />}
                      </span>
                      <span className="flex-1 break-words">{o.label}</span>
                    </button>
                  );
                })
              )}
            </div>
            {selectedOptions.length > 0 && (
              <div
                className={`flex items-center justify-between ${ROW_INSET_X} py-2 border-t border-slate-100 text-xs bg-slate-50`}
              >
                <span className="text-slate-500">
                  {t('common:filter.selectedCount', {
                    count: selectedOptions.length,
                    defaultValue: '{{count}} selected',
                    defaultValue_other: '{{count}} selected',
                  })}
                </span>
                <button
                  type="button"
                  onClick={() => onChange?.([])}
                  className="text-primaryColor hover:text-primaryColor/80 font-medium"
                >
                  {t('common:actions.clearAll', 'Clear all')}
                </button>
              </div>
            )}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
};
